<template>
  <div class="w-full pt-5 pb-5">
    <div v-if="hasModuleAccess">
      <template>
        <div class="flex items-center">
          <div class="border-r border-romanSilver h-6 flex">
            <BackButton label="Back" @onClick="$router.back()" variant="secondary" class="ml-2" />
          </div>
          <h1 class="text-xl text-left font-extrabold mx-4">Agreement Details</h1>
          <Breadcrumb :items="breadcrumbs" />
        </div>
      </template>
      <div style="height: 100%" class="mt-20" v-if="loading">
        <loader size="xxs" :loader-image="false" />
      </div>
      <template v-else>
        <div class="p-5 flex flex-col shadow m-5 rounded-md gap-4">
          <template>
            <div class="flex justify-between items-center">
              <span class="font-bold text-lg leading-5 tracking-normal capitalize text-darkPurple">
                Agreement Details
              </span>
              <template>
                <div
                  class="flex justify-between items-center gap-3 border rounded-md border-desire px-2 py-1 bg-red-100">
                  <Icon class-name="text-desire w-5 h-5" icon-name="icon-info" />
                  <span class="text-desire font-normal text-sm leading-5 capitalize">
                    {{ agreementData.totalEmployees - agreementData.submitted }}
                    Employees Have Not Submitted
                  </span>
                  <span class="text-desire font-black text-xs leading-6 uppercase hover:underline cursor-pointer"
                    @click="complianceModal = true">
                    View
                  </span>
                </div>
              </template>
            </div>
          </template>
          <template>
            <div class="border border-dashed bg-white border-romanSilver rounded-md gap-4 p-3 mt-4">
              <div class="flex justify-between items-center w-full gap-4 pb-2 cursor-pointer"
                @click="handleShowDetails">
                <span class="font-black text-xs leading-5 uppercase tracking-wider text-romanSilver">
                  Performance Details
                </span>
                <span class="w-9/12 border-t border-solid border-romanSilver h-0" />
                <Icon class-name="text-darkPurple mx-auto self-center" size="xms"
                  :icon-name="showDetails ? 'chevronDown' : 'chevronRight'" />
              </div>
              <div class="flex flex-col gap-2" :class="{ hidden: !showDetails }">
                <div class="flex p-3 shadow rounded-md gap-2">
                  <Icon class-name="text-romanSilver self-start pt-1" size="s" icon-name="doc-cycle" />
                  <div class="flex flex-col">
                    <span class="font-black leading-5 text-xs tracking-wide text-darkPurple uppercase">
                      {{ agreementData.cycle }}
                    </span>
                    <span class="font-bold text-sm leading-6 tracking-wide text-jet uppercase">
                      {{ agreementData.description }}
                    </span>
                  </div>
                </div>
                <div class="flex flex-wrap">
                  <div class="flex p-3 shadow rounded-md gap-2 w-2/5 mr-2 mt-2 flex-grow">
                    <Icon class-name="text-romanSilver self-start pt-1" size="xs" icon-name="calendar" />
                    <div class="flex flex-col">
                      <span class="font-black leading-5 text-xs tracking-wide text-carrotOrange uppercase">
                        Period
                      </span>
                      <span class="font-bold text-sm leading-6 tracking-wide text-jet uppercase">
                        {{
                          $DATEFORMAT(
                            new Date(agreementData.period.start),
                            "MMMM dd, yyyy"
                          )
                        }}
                        -
                        {{
                          $DATEFORMAT(
                            new Date(agreementData.period.end),
                            "MMMM dd, yyyy"
                          )
                        }}
                      </span>
                    </div>
                  </div>
                  <div class="flex p-3 shadow rounded-md gap-2 w-2/5 mt-2 flex-grow"
                    v-for="(cycle, index) in agreementCycles" :key="index">
                    <Icon class-name="text-romanSilver self-start pt-1" size="xs" icon-name="reviewicon" />
                    <div class="flex flex-col">
                      <span class="font-black leading-5 text-xs tracking-wide text-blueCrayola uppercase">
                        {{ cycle.name }}
                      </span>
                      <span class="font-bold text-sm leading-6 tracking-wide text-jet uppercase">
                        {{ $DATEFORMAT(new Date(cycle.start), "MMMM dd, yyyy") }}
                        -
                        {{ $DATEFORMAT(new Date(cycle.end), "MMMM dd, yyyy") }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="flex justify-evenly items-center gap-2 mt-2 w-full">
                  <div class="flex flex-col p-3 shadow rounded-md w-full">
                    <span class="font-black leading-5 text-xs tracking-wide text-romanSilver uppercase">
                      Total Employees
                    </span>
                    <span class="font-semibold leading-5 text-xs tracking-wide text-darkPurple uppercase">
                      {{ agreementData.totalEmployees }}
                    </span>
                  </div>
                  <div class="flex flex-col p-3 shadow rounded-md w-full">
                    <span class="font-black leading-5 text-xs tracking-wide text-romanSilver uppercase">
                      Number of Submitted
                    </span>
                    <span class="font-semibold leading-5 text-xs tracking-wide text-mediumSeaGreen uppercase">
                      {{ agreementData.submitted }}
                    </span>
                  </div>
                  <div class="flex flex-col p-3 shadow rounded-md w-full">
                    <span class="font-black leading-5 text-xs tracking-wide text-romanSilver uppercase">
                      Pending
                    </span>
                    <span class="font-semibold leading-5 text-xs tracking-wide text-carrotOrange uppercase">
                      {{ agreementData.totalEmployees - agreementData.submitted }}
                    </span>
                  </div>
                  <div class="flex flex-col p-3 shadow rounded-md w-full">
                    <span class="font-black leading-5 text-xs tracking-wide text-romanSilver uppercase">
                      Compliance
                    </span>
                    <span class="font-semibold leading-5 text-xs tracking-wide text-desire uppercase">
                      ({{
                        (agreementData.submitted / agreementData.totalEmployees) *
                          100
                          ? Math.round(
                            (agreementData.submitted /
                              agreementData.totalEmployees) *
                            100
                          )
                          : "0"
                      }}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <card-footer reloadcard showcalendar :show-bulk-actions="showBulk" :bulk-options="bulkActionOptions"
            @action="performBulkAction($event)" @reload="reload()" @sortType="handleSort($event)"
            @option="handleOptionSelect($event)" @searchResult="handleSearch($event)" />
        </div>
      </template>
      <template v-if="!loading">
        <Table :headers="AppHeader" :items="items" aria-label="advance Table" class="w-full px-5" :has-checkbox="true"
          :has-number="true" :loading="loadingTable" :pagination-list="metaData" style="width: 100%; overflow-x: auto"
          id="printMe" @page="handlePage($event)" @itemsPerPage="handleItemsPerPage($event)"
          @rowSelected="displayRow($event)" page-sync>
          <template v-slot:item="{ item }">
            <div v-if="item.name" class="flex gap-1">
              <div>
                <img class="mr-3" :src="item.data.photo" v-if="item.data.photo" alt="user photo"
                  style="height: 30px; width: 30px; border-radius: 5px" />
                <div style="height: 30px; width: 30px; border-radius: 5px"
                  class="text-blueCrayola mr-3 flex justify-center border text-center font-semibold pt-1" v-else>
                  {{ $getInitials(`${item.data.name}`) }}
                </div>
              </div>
              <div class="flex flex-col whitespace-nowrap font-semibold text-sm leading-5">
                <div class="flex">
                  <span class="text-darkPurple capitalize mr-3">
                    {{ item.data.name }}
                  </span>
                </div>
                <span class="text-blueCrayola text-xs font-bold uppercase">
                  {{ item.data.designation }}
                </span>
              </div>
            </div>
            <div v-if="item.manager" class="flex flex-col whitespace-nowrap font-semibold text-sm leading-5">
              <span class="text-darkPurple capitalize">
                {{ item.data.manager }}
              </span>
            </div>
            <div v-if="item.location" class="flex flex-col whitespace-nowrap font-semibold text-sm leading-5">
              <span class="text-darkPurple capitalize">
                {{ item.data.location }}
              </span>
            </div>
            <div v-if="item.position" class="flex flex-col whitespace-nowrap font-semibold text-sm leading-5">
              <span class="text-darkPurple capitalize">
                {{ item.data.position }}
              </span>
            </div>
            <div v-if="item.level" class="flex flex-col whitespace-nowrap font-semibold text-sm leading-5">
              <span class="text-darkPurple capitalize">
                {{ item.data.level }}
              </span>
            </div>
            <div v-if="item.status">
              <div class="flex flex-col gap-1">
                <div class="flex flex-grow">
                  <Badge :label="item.data.status ? `Submitted` : `Not Submitted`" variant="primary" :background-color="item.data.status
                    ? `rgba(19, 181, 106, 0.15)`
                    : `rgba(234, 60, 83, 0.15)`
                    " :color="item.data.status ? '#13B56A' : '#EA3C53'" class="capitalize" style="
                    margin-right: 12px;
                    font-size: 14px;
                    padding: 12px 10px;
                    border-radius: 5px;
                  " />
                </div>
                <div>
                  <span class="mr-4 text-center" v-if="item.data.dateSubmitted">
                    {{
                      $DATEFORMAT(new Date(item.data.dateSubmitted), "MMMM dd, yyyy")
                    }}
                  </span>
                  <span v-else class="mr-4 text-center">---</span>
                </div>
              </div>
            </div>
            <span v-if="item.id">
              <Menu @click.stop="" class="w-10 h-10" left top="-20" margin="45">
                <template v-slot:title>
                  <Icon icon-name="more_icon" class-name="text-darkPurple mr-2" size="xs" />
                </template>
                <template>
                  <div class="flex flex-col w-48 justify-start items-start">
                    <div class="flex justify-between items-center cursor-pointer p-2" v-if="!item.data.status"
                      @click="setReminder([item.data.id])">
                      <Icon icon-name="bell" class-name="text-blueCrayola mr-2 w-5 h-5" />
                      <span class="text-darkPurple text-sm leading-5 font-normal">Send Reminder</span>
                    </div>
                    <div class="flex justify-between items-center p-2 cursor-pointer" v-if="item.data.status" @click="
                      $router.push({
                        name: 'PerformanceEmployeeDetails',
                        params: { id: item.data.id },
                      })
                      ">
                      <Icon icon-name="icon-eye" class-name="text-blueCrayola mr-2 w-5 h-5" />
                      <span class="text-darkPurple text-sm leading-5 font-normal">View Agreement</span>
                    </div>
                    <div class="flex justify-between items-center cursor-pointer p-2"
                      @click="returnAgreement([item.data.agreementId])" v-if="item.data.status">
                      <Icon icon-name="undo" class-name="text-darkPurple mr-2 w-5 h-5" />
                      <span class="text-darkPurple text-sm leading-5 font-normal">Return to Employee</span>
                    </div>

                  </div>
                </template>
              </Menu>
            </span>
          </template>
        </Table>
      </template>

      <RightSideBar v-if="complianceModal" @submit="setReminder(alertIds)" @close="complianceModal = false"
        submit-button-width="12rem" submit="Send Reminder" button-class="bg-dynamicBackBtn text-white">
        <template v-slot:title>
          <span class="text-darkPurple font-bold text-lg leading-5 p-0">
            Send Reminder
          </span>
        </template>
        <template v-slot:subtitle>
          <div class="pb-5 flex flex-col">
            <span class="text-darkPurple text-base pb-4 leading-5 p-0">
              A reminder will be sent to all selected employees.
            </span>
            <div class="text-darkPurple flex justify-start items-center gap-5">
              <p class="font-normal font-base leading-5 text-jet">
                You have <span class="text-flame font-semibold">{{ differenceDay }}</span> and <span
                  class="text-flame font-semibold">{{ differenceHour }}</span> to complete your performance agreement.
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-1 mb-5">
            <span class="text-sm font-semibold">Message <span class="text-xs font-[500]">(Optional)</span></span>
            <c-textarea label="Message (Optional)" v-model="reminderMessage" placeholder="--Enter--" />
          </div>
        </template>
        <template>
          <div class="flex w-full justify-between items-center">
            <span class="flex gap-1 mt-1 items-center">
              <checkbox v-model="selectAll" checkbox-size="height:16px; width:16px; margin:4px 7px 8px 8px;"
                label-style="color:#333333;" />
              <span class="text-sm font-semibold uppercase -mt-1 leading-5">Select All</span>
            </span>
            <span class="font-normal flex gap-1 leading-5">
              <p class="text-blueCrayola flex text-base font-bold">{{ agreementData.totalEmployees -
                agreementData.submitted
              }}
              </p>
              <p class="text-xs flex text-center mt-1">
                Employees have not submitted
              </p>
            </span>
          </div>
          <div>
            <div class="flex justify-between items-center border-b my-3 border-dashed border-romanSilver"
              v-for="(item, index) in nonSubmitted" :key="index">
              <div class="flex gap-2 py-2">
                <div class="flex items-center">
                  <span class="cursor-pointer">
                    <checkbox checkbox-size="height:16px; width:16px; margin:4px 7px 8px 8px;"
                      label-style="color:#333333; " v-model="item.selected" />
                  </span>
                </div>
                <div class="flex justify-start items-start py-2">
                  <img class="mr-3" :src="item.photo" v-if="item.photo" alt="user photo"
                    style="height: 30px; width: 30px; border-radius: 5px" />
                  <div style="height: 30px; width: 30px; border-radius: 5px"
                    class="text-blueCrayola mr-3 flex justify-center border text-center font-semibold pt-1" v-else>
                    {{ $getInitials(`${item.name}`) }}
                  </div>
                  <div class="flex flex-col justify-start items-start">
                    <span class="font-normal text-base text-darkPurple">{{
                      item.name
                    }}</span>
                    <span class="font-semibold uppercase text-romanSilver" style="font-size: 10px">{{ item.designation
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </RightSideBar>


      <RightSideBar v-if="commentModal" @submit="sendComment" @close="commentModal = false" submit="Save"
        button-class="bg-dynamicBackBtn text-white">
        <template v-slot:title>
          <span class="text-darkPurple font-bold text-lg leading-5 p-0">Comments</span>
        </template>
        <div class="w-full flex">
          <div class="flex flex-col w-full" style="min-height: 70vh">
            <CommentsBox :comments="allComments" @markRead="markAsRead($event)" :loading="loadComments"
              @deleteComment="deleteComments($event)" />
            <div class="-mb-12" style="margin-top: auto">
              <CTextarea placeholder="--Message--" variant="primary" class="mt-2" value="" :row="8" :height="90"
                :col="30" :input="null" v-model="comment" />
            </div>
          </div>
        </div>
      </RightSideBar>




      <RightSideBar v-if="returnAgreementModal" @submit="returnAgreement(alertIds)"
        @close="returnAgreementModal = false" submit-button-width="12rem" submit="Return Agreement"
        button-class="bg-dynamicBackBtn text-white">
        <template v-slot:title>
          <span class="text-darkPurple font-bold text-lg leading-5 p-0">
            Return to Employee
          </span>
        </template>
        <template v-slot:subtitle>
          <div class="pb-5 flex flex-col">
            <span class="text-darkPurple text-base pb-4 leading-5 p-0">
              Return performance agreement to the selected employees.
            </span>
          </div>

          <div class="flex flex-col gap-1 mb-5">
            <span class="text-sm font-semibold">Message <span class="text-xs font-[500]">(Optional)</span></span>
            <c-textarea label="Message (Optional)" placeholder="--Enter--" />
          </div>
        </template>
        <template>
          <div class="flex w-full justify-between items-center">
            <span class="flex gap-1 mt-1 items-center">
              <checkbox v-model="selectSubmitAll" checkbox-size="height:16px; width:16px; margin:4px 7px 8px 8px;"
                label-style="color:#333333;" />
              <span class="text-sm font-semibold uppercase -mt-1 leading-5">Select All</span>
            </span>
            <span class="font-normal flex gap-1 leading-5">
              <p class="text-blueCrayola flex text-base font-bold">{{ agreementData.submitted }}
              </p>
              <p class="text-xs flex text-center mt-1">
                Employees have submitted
              </p>
            </span>
          </div>
          <div>
            <div class="flex justify-between items-center border-b my-3 border-dashed border-romanSilver"
              v-for="(item, index) in submittedAgreements" :key="index">
              <div class="flex gap-2 py-2">
                <div class="flex items-center">
                  <span class="cursor-pointer">
                    <checkbox checkbox-size="height:16px; width:16px; margin:4px 7px 8px 8px;"
                      label-style="color:#333333; " v-model="item.selected" />
                  </span>
                </div>
                <div class="flex justify-start items-start py-2">
                  <img class="mr-3" :src="item.photo" v-if="item.photo" alt="user photo"
                    style="height: 30px; width: 30px; border-radius: 5px" />
                  <div style="height: 30px; width: 30px; border-radius: 5px"
                    class="text-blueCrayola mr-3 flex justify-center border text-center font-semibold pt-1" v-else>
                    {{ $getInitials(`${item.name}`) }}
                  </div>
                  <div class="flex flex-col justify-start items-start">
                    <span class="font-normal text-base text-darkPurple">{{
                      item.name
                    }}</span>
                    <span class="font-semibold uppercase text-romanSilver" style="font-size: 10px">{{ item.designation
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </RightSideBar>


    </div>
    <div v-else>
      <ErrorComponent />
    </div>
  </div>
</template>

<script>
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import Table from "@scelloo/cloudenly-ui/src/components/table";
import Badge from "@scelloo/cloudenly-ui/src/components/badge";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import CardFooter from "@/components/CardFooter";
import Menu from "@/components/Menu";
import RightSideBar from "@/components/RightSideBar";
import Checkbox from "@/components/Checkbox";
import CommentsBox from "@/components/commentsBox";
import paramsMixin from "@/utilities/paramsMixin";
import timeFormatMixin from "@/utilities/timeFormatMixin";
import ErrorComponent from "@/modules/Admin/error403";

export default {
  mixins: [paramsMixin, timeFormatMixin],
  components: {
    BackButton,
    Checkbox,
    ErrorComponent,
    Breadcrumb,
    CardFooter,
    CommentsBox,
    Table,
    CTextarea,
    Badge,
    Menu,
    RightSideBar,
  },
  data() {
    return {
      commentModal: false,
      complianceModal: false,
      returnAgreementModal: false,
      loading: true,
      selectAll: false,
      selectSubmitAll: false,
      calendarFilter: false,
      appraisalCycleId: null,
      showBulk: false,
      commentAgreementId: "",
      hasModuleAccess: false,
      comment: "",
      receiverId: "",
      reminderMessage: '',
      loadingTable: true,
      breadcrumbs: [
        {
          disabled: false,
          text: "Performance",
          href: "Performance",
          id: "Performance",
        },
        {
          disabled: false,
          text: "Performance Agreements",
          href: "agreements",
          id: "PerformanceAgreements",
        },
        {
          disabled: false,
          text: "Agreement Details",
          href: "agreement-details",
          id: "AgreementDetails",
        },
      ],
      agreementData: {
        title: null,
        cycle: null,
        description: null,
        period: { start: null, end: null },
        totalEmployees: null,
        submitted: null,
        compliance: null,
        year: null,
        id: null,
      },
      agreementCycles: [],
      showDetails: true,
      reminderMsg: "",
      appraisalId: null,
      AppHeader: [
        { title: "Name", value: "name" },
        { title: "Level", value: "level", filter: true },
        { title: "Function", value: "position", filter: true },
        { title: "Manager", value: "manager", filter: true },
        { title: "Location", value: "location", filter: true },
        { title: "Status", value: "status" },
        { title: "", value: "id", image: true },
      ],
      items: [],
      itemsPerPage: null,
      numberOfPage: null,
      metaData: {},
      nonSubmitted: [],
      submittedAgreements: [],
      paginationList: {
        page: 1,
        lastPage: 1,
        total: 1,
        from: 1,
        to: 1,
      },
      alertIds: [],
      templatesArr: [],
      allComments: [],
      loadComments: true,
      selectedEmployees: [],
      submittedAgreements: [],
      bulkActionOptions: [
        {
          icon: "bell",
          text: "Send Reminder",
          action: "set Reminder"
        },
        {
          icon: "undo",
          text: "Return to Employee",
          action: "return Agreement"
        },
      ],
      searchQuery: "",
    };
  },

  watch: {
    selectAll(value) {
      this.nonSubmitted.forEach(item => {
        item.selected = value;
      });
      this.updateSelectedEmployees();
    },

    nonSubmitted: {
      handler(val) {
        this.selectAll = val.every(item => item.selected);
        this.updateSelectedEmployees();
      },
      deep: true
    },

    selectSubmitAll(value) {
      this.submittedAgreements.forEach(item => {
        item.selected = value;
      });
      this.updateSubmitSelectedEmployees();
    },

    submitted: {
      handler(val) {
        this.selectAll = val.every(item => item.selected);
        this.updateSubmitSelectedEmployees();
      },
      deep: true
    },
  },

  computed: {
    differenceDay() {
      const now = new Date();

      const targetDate = new Date(this.agreementData.period.end);

      const diffMs = targetDate - now;

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      return `${diffDays} days`;
    },

    differenceHour() {
      const now = new Date();

      const targetDate = new Date(this.agreementData.period.end);

      const diffMs = targetDate - now;

      const diffHours = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );

      return `${diffHours} hours`;
    }
  },

  methods: {
    updateSelectedEmployees() {
      this.selectedEmployees = this.nonSubmitted.filter(item => item.selected);
      this.updateAlertIds();
    },

    updateSubmitSelectedEmployees() {
      this.selectedEmployees = this.submittedAgreements.filter(item => item.selected);
      this.updateAgreementIds();
    },

    updateAlertIds() {
      this.alertIds = this.selectedEmployees
        .map((item) => item.id);
    },

    updateAgreementIds() {
      this.alertIds = this.selectedEmployees
        .map((item) => item.agreementId);
    },

    handleSearch(value) {
      this.searchQuery = `?search=${value}`;
      this.loadingTable = true;
      this.getAgreementUsers("");
    },

    reload() {
      this.loadingTable = true;
      this.getAgreementUsers("");
    },

    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },

    queryUpdate() {
      const pageNumber = this.numberOfPage
        ? `?&page=${this.numberOfPage}`
        : `?&page=${this.metaData.page}`;
      const itemPage = this.itemsPerPage
        ? `?&perPage=${this.itemsPerPage}`
        : "";
      this.getAgreementUsers(`${pageNumber}${itemPage}`);
    },

    displayRow(value) {
      if (value.length === 0) {
        this.showBulk = false;
        return;
      }
      this.showBulk = true;
    },

    performBulkAction(methodFunc) {
      if (methodFunc === "return Agreement") {
        this.returnAgreementModal = true;
      }

      if (methodFunc === "approve Agreement") {
        this.approveAgreement("");
      }

      if (methodFunc === "set Reminder") {
        this.complianceModal = true;
      }
    },

    getComments(agreementId, userId) {
      this.commentModal = true;
      this.allComments = [];
      this.receiverId = userId;
      this.commentAgreementId = agreementId;

      this.loadComments = true;

      this.$_getAgreementComments(agreementId)
        .then((response) => {
          const { comments } = response.data;

          const unread = [];

          this.loadComments = false;

          comments.forEach((comment) => {
            this.allComments.push({
              message: comment.comment,
              time: this.formatCommentTime(comment.date),
              date: this.formatCommentDate(comment.date),
              commentId: comment.id,
              isRead: comment.readStatus,
              receiver: comment.receiver,
              sender: comment.sender,
            });

            if (comment.readStatus === false) {
              unread.push(comment.readStatus);
            }
          });

          this.unreadComments = unread.length;
        })
        .catch((error) => {
          if (error.response.data.message !== "") {
            this.loadComments = false;
          }
        });
    },

    async sendComment() {
      try {
        await this.$handlePrivilege("performanceAgreements", "makeComment");
        const payload = {
          performanceAgreementId: this.commentAgreementId,
          senderId: this.$AuthUser.id,
          receiverId: this.receiverId,
          comment: this.comment,
          sendTo: "admin",
        };

        this.$_postAgreementComments(payload).then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.getComments(this.commentAgreementId, this.receiverId);
          this.comment = "";
        });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },

    deleteComments(id) {
      this.$_deleteAgreementComments(id)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.getComments(this.commentAgreementId, this.receiverId);
        })
        .then((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    formatCommentTime(date) {
      const newDate = new Date(date);
      const hours = newDate.getUTCHours();
      const minutes = newDate.getMinutes();

      return `${1 + ((hours - 1) % 12)}:${minutes
        .toString()
        .padStart(2, "0")} ${hours > 11 ? "PM" : "AM"}`;
    },

    formatCommentDate(date) {
      const newDate = new Date(date);
      return newDate.toDateString();
    },

    returnAgreement(agreementId) {
      const payload = {
        agreementIds: agreementId,
        approvedByAdmin: false,
        approveAndSubmit: false,
      };

      this.$_returnEmployeeAgreement(payload)
        .then((response) => {
          this.$toasted.success(
            `${response.data.message} returned successfully`,
            { duration: 5000 }
          );
          this.items = [];
          this.submittedAgreements = [];
          this.alertIds = [];
          this.returnAgreementModal = false;
          this.getAgreementUsers("");
          this.getAgreements();
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    approveAgreement(agreementId) {
      const payload = {
        agreementIds: agreementId,
        approvedByAdmin: true,
        approveAndSubmit: true,
      };

      this.$_adminApproveAgreement(payload)
        .then((response) => {
          this.$toasted.success(`${response.data.message}`, {
            duration: 5000,
          });
          this.items = [];
          this.getAgreementUsers("");
          this.getAgreements();
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    activateTab(tab) {
      this.activeTab = tab;
      this.$router
        .push({ name: "Mid-Year Review", query: { activeTab: tab } })
        .catch(() => { });
    },
    handleCommentModal() {
      this.commentModal = !this.commentModal;
    },

    handleCalendarFilter() {
      this.calendarFilter = true;
    },

    async setReminder(id) {
      try {
        await this.$handlePrivilege(
          "performanceAgreements",
          "sendPerformanceContractReminder"
        );
        const payload = {
          message: `${this.reminderMessage === ""
            ? this.reminderMsg
            : this.reminderMessage
            }`,
          userIds: id,
          appraisalId: this.appraisalCycleId
        };

        this.$_agreementReminder(payload)
          .then((response) => {
            this.$toasted.success(response.data.message, { duration: 5000 });
            this.complianceModal = false;
          })
          .catch((error) => {
            this.$toasted.error(error.response.data.message, {
              duration: 5000,
            });
          });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },

    handleShowDetails() {
      this.showDetails = !this.showDetails;
    },

    cycleName(templateId) {
      const template = this.templateArr.find(t => t.id === templateId);
      return template ? template.title : "";
    },

    cycleDescription(templateId) {
      const template = this.templateArr.find(t => t.id === templateId);
      return template ? template.description : "";
    },

    getAgreements() {
      this.$_getOrgPerformanceAgreement(this.searchQuery).then((response) => {
        const result = response.data.data;

        result.forEach((info) => {
          if (info.agreementDetail.id === this.$route.params.id) {
            this.agreementData.title = this.cycleName(
              info.agreementDetail.templateLinkId
            );
            this.agreementData.cycle = this.cycleName(
              info.agreementDetail.templateLinkId
            );
            this.agreementData.description = this.cycleDescription(
              info.agreementDetail.templateLinkId
            );

            this.agreementData.totalEmployees = info.totalEmployees;
            this.agreementData.submitted = info.SubmittedAgreement;
            this.agreementData.compliance = info.compliance;
            this.agreementData.year = info.agreementDetail.year;
            this.agreementData.id = info.agreementDetail.id;

            this.loading = false;
          }
        });
      });
    },

    async goToAppraisal(id, name, agreementDataId) {
      try {
        await this.$handlePrivilege("performanceAgreements", "gotoAppraisal");
        this.$router.push({
          name: "PerformanceEmployeeAppraisal",
          params: {
            year: this.$route.params.year,
            userId: id,
            cycle: name,
            goalId: agreementDataId,
          },
        });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },

    getTemplate() {
      this.$_getPerfomanceTemplate("").then((response) => {
        const templatesArr = response.data.PerformanceTemplate;
        this.loading = false;
        this.templateArr = templatesArr;

        const currentYear = new Date().getFullYear();

        const template = templatesArr.find(t => t.year === currentYear);

        if (template) {
          this.agreementData.period.start = this.setDates(
            template.appraisal_cycle.agreement_start
          ).toISOString();

          this.agreementData.period.end = this.setDates(
            template.appraisal_cycle.agreement_end
          ).toISOString();
        }

      });
    },

    getAppraisalCycle() {
      this.$_getAppraisalCycle(this.agreementQuery()).then((response) => {
        const appraisal = response.data.AppraisalCycle;
        this.appraisalCycleId = appraisal[0].id;
        if (appraisal[0].cycles.length > 0) {
          appraisal[0].cycles.map((cycle) => {
            this.agreementCycles.push({
              name: cycle.name,
              start: this.setDates(cycle.appraisal_starts).toISOString(),
              end: this.setDates(cycle.appraisal_ends).toISOString(),
            });
            return {};
          });
        }

        this.getAgreements();
      });
    },

    getAgreementUsers(query) {
      this.items = [];
      this.$_getOrgAgreementEmployees(query, this.searchQuery)
        .then((response) => {
          const { employees } = response.data;
          this.metaData = response.data.meta;
          employees.forEach((employee) => {
            this.items.push({
              name: `${employee.fname} ${employee.lname}`,
              photo: employee.photo,
              location: employee.location,
              level: employee.level,
              position: employee.function,
              designation: employee.designation,
              manager: employee.lineManager
                ? `${employee.lineManager.fname} ${employee.lineManager.lname}`
                : "---",
              status: employee.submitted,
              agreementId: employee.agreementId,
              dateSubmitted: employee.dateSubmitted,
              approved: employee.approved,
              id: employee.userId,
            });
          });

          employees.filter((employee) => {
            if (!employee.submitted) {
              this.nonSubmitted.push({
                name: `${employee.fname} ${employee.lname}`,
                photo: employee.photo,
                designation: employee.designation,
                id: employee.userId,
              });

              this.alertIds.push(employee.userId);
            }

            if (employee.submitted) {
              this.submittedAgreements.push({
                name: `${employee.fname} ${employee.lname}`,
                photo: employee.photo,
                designation: employee.designation,
                id: employee.userId,
                agreementId: employee.agreementId
              });
            }
            return {};
          });

          this.loadingTable = false;
        })
        .catch(() => {
          this.loadingTable = false;
        });
    },

    async viewStatusReports(id, year) {
      try {
        await this.$handlePrivilege(
          "performanceAgreements",
          "viewStatusReport"
        );
        this.$router.push({
          name: "EmployeeStatusReports",
          params: { id, year },
        });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },

    getPerformanceSettings() {
      this.$_getPerformanceGeneralSetting().then((response) => {
        const reminder = response.data.PerformanceSettings;
        this.reminderMsg = reminder[0].reminderMessage;
      });
    },
  },

  async mounted() {
    try {
      await this.$handlePrivilege(
        "performanceAgreements",
        "viewPerformanceContracts"
      );
      this.hasModuleAccess = true;
      this.getAppraisalCycle();
      this.getAgreementUsers("");
      this.getTemplate();
      this.getPerformanceSettings();
    } catch (error) {
      this.hasModuleAccess = false;
    }
  },
};
</script>

<style scoped>
.submitted {
  background: rgba(19, 181, 106, 0.15);
  color: #27ae60;
  text-align: center;
  padding: 5px;
  border-radius: 5px;
}

.not_submitted {
  background: rgba(234, 60, 83, 0.15);
  color: #ea3c53;
  text-align: center;
  padding: 5px;
  border-radius: 5px;
}
</style>

<style>
.icon {
  width: 24px;
  height: 24px;
  padding: 3px;
}
</style>
